/**
 * PaddingElement - Visual cross-layout for padding fields (top, right, bottom, left)
 *
 * Features:
 * - Visual cross layout with directional labels
 * - 4 compact number inputs
 * - Collapsible (expanded by default)
 * - Aligned with other style fields (left margin)
 *
 * Layout:
 *              top
 *               0
 *  left  0  ─────────  0  right
 *               0
 *             bottom
 *
 * Sprint: screen-editor-header
 * Task: PaddingElement component
 */

import React, { useCallback, useState } from 'react';
import { Box, Typography, Tooltip, Collapse, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { StyledNumberInput, WithGuideHelp } from '@modules/ui-components';
import type { GuideService } from '@modules/firebase-core/services/GuideService';
import {
  fieldLabelSx,
  SHOW_FIELD_PATH_TOOLTIP,
} from '../../config/editorFieldStyles';
import { FIELD_MARGIN_BOTTOM, FIELD_GAP_SMALL } from '../../config/ui';

/**
 * Padding values object
 */
export interface PaddingValues {
  paddingTop: number;
  paddingRight: number;
  paddingBottom: number;
  paddingLeft: number;
}

/**
 * Props for PaddingElement component
 */
export interface PaddingElementProps {
  /** Base path for padding fields (e.g., 'footer.styles') */
  basePath: string;

  /** Current padding values */
  values: PaddingValues;

  /** Callback when a padding value changes - receives full path and value */
  onChange: (path: string, value: number) => void;

  /** Whether all inputs are disabled */
  disabled?: boolean;

  /** Label to display above the cross layout */
  label?: string;

  /** Optional guide key for contextual help */
  guideKey?: string;

  /** Optional guide service for rendering guide help icon */
  guideService?: GuideService;

  /** Whether section is expanded by default */
  defaultExpanded?: boolean;

  /** Minimum value for inputs (default 0, set negative for margins) */
  min?: number;

  /** data-testid for the main container */
  'data-testid'?: string;
}

/** Width of each number input (compact) */
const INPUT_WIDTH = 32;

/** Shared styles for number inputs */
const inputSx = {
  width: INPUT_WIDTH,
  '& .MuiInputBase-input': {
    textAlign: 'center',
    padding: '2px 4px',
    fontSize: '11px',
  },
};

/** Label style for direction labels */
const directionLabelSx = {
  fontSize: '9px',
  color: 'text.secondary',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

/**
 * PaddingElement - Visual cross-layout for padding fields
 *
 * Provides an intuitive visual representation of padding with
 * inputs positioned in a cross pattern with directional labels.
 */
export const PaddingElement: React.FC<PaddingElementProps> = ({
  basePath,
  values,
  onChange,
  disabled = false,
  label = 'Padding',
  guideKey,
  guideService,
  defaultExpanded = true,
  min: minValue = 0,
  'data-testid': dataTestId,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Handle value change for a specific padding direction
  const handleChange = useCallback(
    // StyledNumberInput emits `null` for a cleared input; it has always been
    // forwarded verbatim — keep that runtime behavior (cast, no coercion).
    (direction: keyof PaddingValues) => (newValue: number | null) => {
      const path = `${basePath}.${direction}`;
      onChange(path, newValue as number);
    },
    [basePath, onChange]
  );

  // Get safe number value (handle null/undefined)
  const getSafeValue = (val: number | null | undefined): number => {
    return typeof val === 'number' ? val : 0;
  };

  // Toggle expanded state
  const handleToggle = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  // Label content (reused in both branches)
  const labelContent = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        userSelect: 'none',
      }}
      onClick={handleToggle}
    >
      <IconButton
        size="small"
        sx={{
          p: 0,
          mr: 0.5,
          transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
          transition: 'transform 0.2s',
        }}
      >
        <ExpandMoreIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Tooltip
        title={SHOW_FIELD_PATH_TOOLTIP ? basePath : ''}
        placement="top"
        arrow
        disableHoverListener={!SHOW_FIELD_PATH_TOOLTIP}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={fieldLabelSx}
          data-testid={basePath}
          aria-label={basePath}
        >
          {label}
        </Typography>
      </Tooltip>
    </Box>
  );

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: FIELD_GAP_SMALL,
        mb: FIELD_MARGIN_BOTTOM,
      }}
    >
      {/* Collapsible Header */}
      {guideKey && guideService ? (
        <WithGuideHelp
          guideKey={guideKey}
          guideService={guideService}
          data-testid={`guide-anchor-${guideKey}`}
          position="inline-end"
          iconSize="small"
        >
          {labelContent}
        </WithGuideHelp>
      ) : (
        labelContent
      )}

      {/* Collapsible Cross Layout */}
      <Collapse in={expanded} unmountOnExit>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: INPUT_WIDTH * 2 + 8, // Fixed width for symmetric centering
          }}
        >
          {/* Top row: label + input */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography sx={directionLabelSx}>top</Typography>
            <Box data-testid="padding-top">
              <StyledNumberInput
                value={getSafeValue(values.paddingTop)}
                onChange={handleChange('paddingTop')}
                disabled={disabled}
                size="small"
                variant="standard"
                min={minValue}
                max={100}
                step={1}
                sx={inputSx}
                data-testid={`${basePath}.paddingTop`}
                aria-label={`${basePath}.paddingTop`}
              />
            </Box>
          </Box>

          {/* Middle Row: Left + Right */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            {/* Left: label + input */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography sx={directionLabelSx}>left</Typography>
              <Box data-testid="padding-left">
                <StyledNumberInput
                  value={getSafeValue(values.paddingLeft)}
                  onChange={handleChange('paddingLeft')}
                  disabled={disabled}
                  size="small"
                  variant="standard"
                  min={minValue}
                  max={100}
                  step={1}
                  sx={inputSx}
                  data-testid={`${basePath}.paddingLeft`}
                  aria-label={`${basePath}.paddingLeft`}
                />
              </Box>
            </Box>

            {/* Right: label + input */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography sx={directionLabelSx}>right</Typography>
              <Box data-testid="padding-right">
                <StyledNumberInput
                  value={getSafeValue(values.paddingRight)}
                  onChange={handleChange('paddingRight')}
                  disabled={disabled}
                  size="small"
                  variant="standard"
                  min={minValue}
                  max={100}
                  step={1}
                  sx={inputSx}
                  data-testid={`${basePath}.paddingRight`}
                  aria-label={`${basePath}.paddingRight`}
                />
              </Box>
            </Box>
          </Box>

          {/* Bottom row: input + label */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box data-testid="padding-bottom">
              <StyledNumberInput
                value={getSafeValue(values.paddingBottom)}
                onChange={handleChange('paddingBottom')}
                disabled={disabled}
                size="small"
                variant="standard"
                min={minValue}
                max={100}
                step={1}
                sx={inputSx}
                data-testid={`${basePath}.paddingBottom`}
                aria-label={`${basePath}.paddingBottom`}
              />
            </Box>
            <Typography sx={directionLabelSx}>bottom</Typography>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default PaddingElement;
